import { useContext, useEffect, useState } from "react";
import CustomSpinner from "../components/CustomSpinner";
import UserContext from "../context/UserContext";
import categoryService from "../services/categoryService";
import { Navigate } from "react-router-dom";

const COLORS = [
  { hex: "#E24B4A", light: "#FCEBEB", label: "Rojo" },
  { hex: "#D85A30", light: "#FAECE7", label: "Coral" },
  { hex: "#BA7517", light: "#FAEEDA", label: "Ámbar" },
  { hex: "#639922", light: "#EAF3DE", label: "Verde" },
  { hex: "#1D9E75", light: "#E1F5EE", label: "Teal" },
  { hex: "#185FA5", light: "#E6F1FB", label: "Azul" },
  { hex: "#7F77DD", light: "#EEEDFE", label: "Violeta" },
  { hex: "#D4537E", light: "#FBEAF0", label: "Rosa" },
  { hex: "#888780", light: "#F1EFE8", label: "Gris" },
  { hex: "#534AB7", light: "#CECBF6", label: "Índigo" },
];

const EMOJIS = [
  "🛒", "🏠", "🚗", "💊", "🎉", "🍔", "✈️", "📚",
  "💼", "👕", "🎮", "💡", "🐾", "💰", "🏋️", "🎵",
];

const emptyCategory = () => ({
  name: "",
  type: "expense",
  icon: EMOJIS[0],
  color: COLORS[0].hex,
  colorLight: COLORS[0].light,
});

export default function Categories() {
  const { user } = useContext(UserContext);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [newCategory, setNewCategory] = useState(emptyCategory());
  const [selColorIdx, setSelColorIdx] = useState(0);
  const [selEmojiIdx, setSelEmojiIdx] = useState(0);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const res = await categoryService.getCategories();
      setCategories(res.data);
    } catch (error) {
      console.error("Error cargando categorías:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    loadCategories();
  }, [user]);

  const handlePickColor = (idx) => {
    setSelColorIdx(idx);
    setNewCategory((prev) => ({
      ...prev,
      color: COLORS[idx].hex,
      colorLight: COLORS[idx].light,
    }));
  };

  const handlePickEmoji = (idx) => {
    setSelEmojiIdx(idx);
    setNewCategory((prev) => ({ ...prev, icon: EMOJIS[idx] }));
  };

  const resetCategoryForm = () => {
    setEditingCategoryId(null);
    setNewCategory(emptyCategory());
    setSelColorIdx(0);
    setSelEmojiIdx(0);
  };

  const handleOpenCreateForm = () => {
    resetCategoryForm();
    setShowForm(true);
  };

  const handleEditCategory = (cat) => {
    const colorIdx = COLORS.findIndex((color) => color.hex === cat.color);
    const emojiIdx = EMOJIS.findIndex((emoji) => emoji === cat.icon);

    setEditingCategoryId(cat.id);
    setNewCategory({
      ...emptyCategory(),
      ...cat,
      name: cat.name || "",
      type: cat.type || "expense",
      icon: cat.icon || EMOJIS[0],
      color: cat.color || COLORS[0].hex,
      colorLight: cat.colorLight || COLORS[0].light,
    });
    setSelColorIdx(colorIdx >= 0 ? colorIdx : 0);
    setSelEmojiIdx(emojiIdx >= 0 ? emojiIdx : 0);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!newCategory.name.trim()) return;
    try {
      setLoading(true);
      if (editingCategoryId) {
        await categoryService.updateCategory(editingCategoryId, newCategory);
      } else {
        await categoryService.createCategory(newCategory);
      }
      await loadCategories();
      resetCategoryForm();
      setShowForm(false);
    } catch (error) {
      console.error("Error guardando categoría:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar la categoría "${name}"?`)) return;
    try {
      setLoading(true);
      await categoryService.deleteCategory(id);
      await loadCategories();
    } catch (error) {
      console.error("Error eliminando categoría:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetCategoryForm();
  };

  if (!user) return <Navigate to='/' replace />;

  const expenses = categories.filter((c) => c.type === "expense");
  const incomes = categories.filter((c) => c.type === "income");

  const previewColor = COLORS[selColorIdx];

  return (
    <>
      {loading && <CustomSpinner />}
      <div className="min-h-[91vh] banner-1 px-4 py-6 text-slate-100 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-3xl border border-white/10 bg-slate-900/80 p-4 shadow-2xl shadow-black/20 sm:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h5 className="text-xl font-semibold">Mis Categorías</h5>
            <button
              type="button"
              onClick={handleOpenCreateForm}
              className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-4 py-2 text-sm font-medium text-sky-300 transition hover:bg-sky-500/20"
            >
              + Nueva Categoría
            </button>
          </div>

          {showForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur-sm">
              <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-slate-900 p-4 shadow-2xl shadow-black/30 sm:p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">{editingCategoryId ? "Editar categoría" : "Nueva categoría"}</h3>
                  <button type="button" onClick={handleCloseForm} className="rounded-full p-2 text-slate-300 transition hover:bg-white/10">✕</button>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-300">Nombre</label>
                    <input
                      type="text"
                      placeholder="Ej: Supermercado"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none ring-0"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-300">Tipo</label>
                    <select
                      value={newCategory.type}
                      onChange={(e) => setNewCategory((prev) => ({ ...prev, type: e.target.value }))}
                      className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none"
                    >
                      <option value="expense">Gasto</option>
                      <option value="income">Ingreso</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium text-slate-300">Emoji / Ícono</label>
                  <div className="flex flex-wrap gap-2">
                    {EMOJIS.map((emoji, idx) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => handlePickEmoji(idx)}
                        title={emoji}
                        className={`flex h-9 w-9 items-center justify-center rounded-lg border text-lg transition ${selEmojiIdx === idx ? "scale-110" : "scale-100"}`}
                        style={{
                          borderColor: selEmojiIdx === idx ? previewColor.hex : "rgba(255,255,255,0.15)",
                          backgroundColor: selEmojiIdx === idx ? previewColor.light : "rgba(255,255,255,0.05)",
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium text-slate-300">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {COLORS.map((color, idx) => (
                      <button
                        key={color.hex}
                        type="button"
                        onClick={() => handlePickColor(idx)}
                        title={color.label}
                        aria-label={color.label}
                        className={`h-7 w-7 rounded-full border-2 transition ${selColorIdx === idx ? "scale-110 border-white" : "border-transparent"}`}
                        style={{ backgroundColor: color.hex }}
                      />
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium text-slate-300">Vista previa</label>
                  <span
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium"
                    style={{ backgroundColor: previewColor.light, color: previewColor.hex }}
                  >
                    {newCategory.icon} {newCategory.name || "Mi categoría"}
                  </span>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                  <button type="button" onClick={handleCloseForm} className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:bg-slate-800">Cancelar</button>
                  <button type="button" onClick={handleSave} disabled={!newCategory.name.trim()} className="rounded-lg bg-sky-500 px-3 py-2 text-sm font-medium text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-50">{editingCategoryId ? "Actualizar" : "Guardar"}</button>
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                <span className="text-rose-400">↓</span> Gastos
              </div>
              {expenses.length === 0 ? (
                <p className="text-sm text-slate-400">Sin categorías de gasto.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {expenses.map((cat) => (
                    <CategoryCard key={cat.id} cat={cat} onDelete={handleDelete} onEdit={handleEditCategory} />
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                <span className="text-emerald-400">↑</span> Ingresos
              </div>
              {incomes.length === 0 ? (
                <p className="text-sm text-slate-400">Sin categorías de ingreso.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {incomes.map((cat) => (
                    <CategoryCard key={cat.id} cat={cat} onDelete={handleDelete} onEdit={handleEditCategory} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function CategoryCard({ cat, onDelete, onEdit }) {
  const [hovered, setHovered] = useState(false);

  const colorLight = cat.colorLight || COLORS.find((c) => c.hex === cat.color)?.light || "#f0f0f0";

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 transition ${hovered ? "border-white/20 bg-white/10" : "border-white/10 bg-white/5"}`}
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-lg" style={{ backgroundColor: colorLight }}>
        {cat.icon}
      </div>

      <span className="flex-1 truncate text-sm font-medium text-white">
        {cat.name}
      </span>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(cat)}
          aria-label={`Editar ${cat.name}`}
          className={`flex h-6 w-6 items-center justify-center rounded-full text-base transition ${hovered ? "bg-sky-500/20 text-sky-400" : "text-transparent"}`}
        >
          ✎
        </button>
        <button
          onClick={() => onDelete(cat.id, cat.name)}
          aria-label={`Eliminar ${cat.name}`}
          className={`flex h-6 w-6 items-center justify-center rounded-full text-base transition ${hovered ? "bg-rose-500/20 text-rose-400" : "text-transparent"}`}
        >
          ×
        </button>
      </div>
    </div>
  );
}
